import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { isAbort } from "../utils";
import { savePdfUrls } from "Redux/Actions/Index";
import { Loader } from "Shared/Common/Loader";
import useLang from "Shared/hooks/useLanguage";
import { ShowBlobInViewerV2 } from "Services/Requisition/RequisitionTypeService";

type FileData = { uri: string; fileType: string; fileName?: string };

interface Props {
  url: string;
  title?: string;
  onClose: () => void;
}

const fileNameOf = (url: string) => {
  const clean = url.split("?")[0];
  return decodeURIComponent(clean.substring(clean.lastIndexOf("/") + 1)) || url;
};

const MIN_SCALE = 0.5;
const MAX_SCALE = 3;

// Result file viewer shown in place of the detail panel (same file pipeline as /docs-viewer).
const InlineDocViewer = ({ url, title, onClose }: Props) => {
  const { t } = useLang();
  const dispatch = useDispatch();
  const [files, setFiles] = useState<FileData[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    let alive = true;
    setFiles(null);
    setFailed(false);
    setScale(1);
    ShowBlobInViewerV2(url.split("|!"))
      .then((res: { data?: { Data?: FileData[] } }) => {
        if (alive) setFiles(res?.data?.Data ?? []);
      })
      .catch((e: unknown) => {
        if (alive && !isAbort(e)) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [url]);

  const pdfs = (files ?? []).filter((f) => f.fileType?.toLowerCase() === "pdf");
  const images = (files ?? []).filter((f) =>
    /(jpe?g|png|gif|webp)$/i.test(f.fileType ?? "")
  );

  const renderBody = () => {
    if (failed)
      return (
        <div className="text-center text-gray-600 py-10">
          <i className="bi bi-exclamation-circle fs-2x d-block mb-2 text-danger"></i>
          {t("Could not load the document.")}
        </div>
      );
    if (!files)
      return (
        <div className="py-10">
          <Loader />
        </div>
      );
    if (pdfs.length)
      return (
        <Worker workerUrl="/pdf.worker.min.js">
          {pdfs.map((f, i) => (
            <div key={`${f.uri}-${scale}`} className={i ? "mt-4" : ""}>
              <Viewer fileUrl={f.uri} defaultScale={scale} />
            </div>
          ))}
        </Worker>
      );
    if (images.length)
      return images.map((f) => (
        <img
          key={f.uri}
          src={f.uri}
          alt={fileNameOf(f.uri)}
          style={{ width: `${scale * 100}%`, maxWidth: "none" }}
        />
      ));
    return (
      <div className="text-center text-gray-600 py-10">
        <i className="bi bi-file-earmark fs-2x d-block mb-2"></i>
        {t("This file can't be previewed here. Open it in a new tab.")}
      </div>
    );
  };

  const canZoom = pdfs.length > 0 || images.length > 0;

  return (
    <aside className="vr2-detail" aria-label={t("Document viewer")}>
      <div className="vr2-detail-head">
        <i className="bi bi-file-earmark-pdf text-danger fs-5 flex-shrink-0"></i>
        <span className="vr2-head-name" title={fileNameOf(url)}>
          {title || fileNameOf(url)}
        </span>
        <div className="d-flex align-items-center gap-1 ms-auto flex-shrink-0">
          {canZoom && (
            <>
              <button
                type="button"
                className="btn btn-icon btn-sm h-28px w-28px btn-active-light-primary"
                aria-label={t("Zoom out")}
                title={t("Zoom out")}
                disabled={scale <= MIN_SCALE}
                onClick={() =>
                  setScale((s) => Math.max(MIN_SCALE, +(s - 0.2).toFixed(1)))
                }
              >
                <i className="bi bi-zoom-out fs-6"></i>
              </button>
              <span className="vr2-zoom-label">{Math.round(scale * 100)}%</span>
              <button
                type="button"
                className="btn btn-icon btn-sm h-28px w-28px btn-active-light-primary"
                aria-label={t("Zoom in")}
                title={t("Zoom in")}
                disabled={scale >= MAX_SCALE}
                onClick={() =>
                  setScale((s) => Math.min(MAX_SCALE, +(s + 0.2).toFixed(1)))
                }
              >
                <i className="bi bi-zoom-in fs-6"></i>
              </button>
              <span className="vr2-head-sep" aria-hidden="true" />
            </>
          )}
          <Link
            to="/docs-viewer"
            target="_blank"
            className="btn btn-icon btn-sm h-28px w-28px btn-active-light-primary"
            aria-label={t("Open in new tab")}
            title={t("Open in new tab")}
            onClick={() => dispatch(savePdfUrls(url))}
          >
            <i className="bi bi-box-arrow-up-right fs-6"></i>
          </Link>
          <button
            type="button"
            className="btn btn-icon btn-sm h-28px w-28px btn-active-light-primary"
            aria-label={t("Back to details")}
            title={t("Back to details")}
            onClick={onClose}
          >
            <i className="bi bi-x-lg fs-6"></i>
          </button>
        </div>
      </div>
      <div className="vr2-doc-body">{renderBody()}</div>
    </aside>
  );
};

export default InlineDocViewer;
